import React from 'react';
import {
  Box, Typography, List, ListItem, ListItemText, Chip,
  Alert, AlertTitle, CircularProgress, Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

const PROBE_LABELS = {
  homepage:      'Homepage',
  wp_login:      'WordPress login page',
  rest_pretty:   'REST API (/wp-json/)',
  rest_fallback: 'REST API fallback (?rest_route=/)',
  plugin:        'Nerv plugin endpoint',
};

const CLASSIFICATION_SEVERITY = {
  ok: 'success',
  cloudflare_blocking: 'warning',
  site_unreachable: 'error',
  not_wordpress: 'error',
  rest_api_disabled: 'warning',
  permalinks_misconfigured: 'info',
  plugin_not_installed: 'info',
};

function probeIsOk(p) {
  if (!p.ok) return false;
  if (p.name === 'rest_pretty' || p.name === 'rest_fallback' || p.name === 'plugin') {
    return p.status === 200 && (p.content_type || '').toLowerCase().startsWith('application/json');
  }
  return p.status >= 200 && p.status < 400;
}

function ProbeRow({ probe }) {
  const ok = probeIsOk(probe);
  return (
    <ListItem disableGutters sx={{ py: 0.5 }}>
      {ok
        ? <CheckCircleIcon data-testid="probe-ok" sx={{ color: 'success.main', mr: 1 }} />
        : <CancelIcon data-testid="probe-fail" sx={{ color: 'error.main', mr: 1 }} />}
      <ListItemText
        primary={<Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 13 }}>{probe.name}</Typography>}
        secondary={
          <Typography component="span" variant="caption" color="text.secondary">
            {PROBE_LABELS[probe.name] || probe.name} — HTTP {probe.status || 'no response'}
            {probe.note ? ` · ${probe.note}` : ''}
          </Typography>
        }
      />
    </ListItem>
  );
}

export default function DiagnosticPanel({ result, loading }) {
  if (loading || !result) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Running diagnostic…</Typography>
      </Box>
    );
  }

  const severity = CLASSIFICATION_SEVERITY[result.classification] || 'info';

  return (
    <Box>
      <Alert severity={severity} sx={{ mb: 2 }}>
        <AlertTitle>{result.title}</AlertTitle>
        <Typography variant="body2">{result.suggested_fix}</Typography>
      </Alert>

      <Typography variant="overline" color="text.secondary">Probes</Typography>
      <List dense disablePadding>
        {result.probes.map((p) => <ProbeRow key={p.name} probe={p} />)}
      </List>

      {result.cf_token_present && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Cloudflare API token:</Typography>
            <Chip
              size="small"
              label={result.cf_token_valid ? 'Valid' : 'Invalid'}
              color={result.cf_token_valid ? 'success' : 'error'}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
